import { translationKey } from '../i18n';
import { localize } from '../i18n';
import DialogTitle from './ui/FitDialogTitle';
import SingleLineText from './ui/SingleLineText';
import Button from './ui/FitButton';
import Dialog from './StableDialog';
import React, { useState, useLayoutEffect } from 'react';
import { Box, Typography, DialogContent, DialogActions } from '@mui/material';
import BottomSheet from './BottomSheet';
import MedicationTimesEditor, { normalizeMedicationTimes } from '../pages/medication-form/MedicationTimesEditor';

function formatTimesKorean(times = []) {
    return times.map(time => {
        const [hour, minute] = time.split(':');
        const h = Number(hour);
        const period = localize(h < 12 ? '오전' : '오후');
        const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        return `${period} ${h12}:${minute}`;
    }).join(', ');
}

/**
 * 알림 시간 편집 시트 — 확인 페이지(StepConfirm)의 알림 카드에서 ✏ 클릭 시 노출.
 *
 * 동작:
 * - 시트가 열리면 외부 form의 시간/같이먹는약 상태를 로컬 staging에 복제
 * - 사용자가 시트 안에서 시간 추가/삭제/수정해도 외부 form은 즉시 변경 안 됨
 * - "저장" 누르면 onSave에 patch 전달 → 부모가 form 또는 서버 반영
 * - 닫기(드래그/배경 탭) 시 staging 폐기
 */
export default function EditTimesSheet({ open, form, existingMedications, onClose, onSave }) {
    const [staging, setStaging] = useState(null);
    const [pendingPreset, setPendingPreset] = useState(null);
    const [warning, setWarning] = useState('');

    useLayoutEffect(() => {
        if (open) {
            setStaging({
                times: [...(form.times || ['08:00'])],
                alertType: form.alertType,
                alarmVolume: form.alarmVolume,
                reminderEnabled: form.reminderEnabled,
                reminderIntervalMinutes: form.reminderIntervalMinutes,
                maxReminderMinutes: form.maxReminderMinutes,
                matchedMedicationName: form.matchedMedicationName,
            });
            setPendingPreset(null);
            setWarning('');
        }
    }, [open, form]);

    if (!staging) return <BottomSheet open={open} onClose={onClose}>{null}</BottomSheet>;

    const updateStaging = (updater) => {
        setStaging(prev => typeof updater === 'function' ? updater(prev) : updater);
    };

    const handleSave = () => {
        // 같이 먹는 약 칩으로 alertType/볼륨/다시알림까지 변경됐을 수 있어 상위에 모두 전달.
        onSave({
            times: staging.times,
            alertType: staging.alertType,
            alarmVolume: staging.alarmVolume,
            reminderEnabled: staging.reminderEnabled,
            reminderIntervalMinutes: staging.reminderIntervalMinutes,
            maxReminderMinutes: staging.maxReminderMinutes,
            matchedMedicationName: staging.matchedMedicationName,
        });
        onClose();
    };

    const closeSheet = () => {
        setPendingPreset(null);
        onClose();
    };

    const applyPendingPreset = () => {
        if (pendingPreset) {
            updateStaging(prev => ({ ...prev, times: normalizeMedicationTimes(pendingPreset.times) }));
        }
        setPendingPreset(null);
    };

    return (
        <>
            <BottomSheet open={open} onClose={closeSheet} maxHeight="78vh" footer={
                <>
                    <Button fullWidth onMouseDown={(e) => e.preventDefault()} onClick={handleSave} sx={{
                        bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 3, py: 1.45,
                        fontSize: '1.05rem', fontWeight: 700, boxShadow: 'none',
                        '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                    }}>{localize("저장")}</Button>
                </>
            }>
                <SingleLineText sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', mb: 2 }}>{localize("시간 수정")}</SingleLineText>
                <MedicationTimesEditor
                    value={staging.times || ['08:00']}
                    onChange={(times) => updateStaging(prev => ({ ...prev, times }))}
                    onPresetReplaceRequest={(preset) => setPendingPreset(preset)}
                    onWarning={setWarning}
                    existingMedications={existingMedications}
                />
            </BottomSheet>
            <Dialog open={!!warning} onClose={() => setWarning('')} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 900, color: '#1a1a1a' }}>{warning.includes(translationKey('10번')) ? localize("시간을 더 추가할 수 없어요") : localize("시간 설정 안내")}</DialogTitle>
                <DialogContent><Typography sx={{ color: '#555', fontWeight: 650 }}>{warning}</Typography></DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}><Button onClick={() => setWarning('')} sx={{ minHeight: 46, px: 3, bgcolor: '#ffe566', color: '#5a4a00', fontWeight: 850, borderRadius: 2.5, '&:hover': { bgcolor: '#ffdf33' } }}>{localize("확인")}</Button></DialogActions>
            </Dialog>
            <Dialog
                open={!!pendingPreset}
                onClose={() => setPendingPreset(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#1a1a1a', pb: 0.8 }}>{localize("알림 시간을 바꿀까요?")}</DialogTitle>
                <DialogContent sx={{ pt: 0.5 }}>
                    <SingleLineText sx={{ color: '#666', fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.55, wordBreak: 'keep-all' }}>{localize("직접 설정한 시간이 아래 시간으로 바뀌어요.")}</SingleLineText>
                    {pendingPreset?.times && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65, mt: 1 }}>
                            {pendingPreset.times.map(time => (
                                <Box
                                    key={time}
                                    component="span"
                                    sx={{
                                        px: 0.9,
                                        py: 0.45,
                                        borderRadius: '999px',
                                        bgcolor: '#fffbeb',
                                        border: '1px solid #fde68a',
                                        color: '#5a4a00',
                                        fontSize: '0.88rem',
                                        fontWeight: 850,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {formatTimesKorean([time])}
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, p: 2, pt: 0 }}>
                    <Button
                        onClick={() => setPendingPreset(null)}
                        sx={{
                            minHeight: 48,
                            borderRadius: 2.5,
                            bgcolor: '#f3f4f6',
                            color: '#555',
                            fontSize: '0.98rem',
                            fontWeight: 850,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#e5e7eb', boxShadow: 'none' },
                        }}
                    >{localize("취소")}</Button>
                    <Button
                        onClick={applyPendingPreset}
                        variant="contained"
                        sx={{
                            minHeight: 48,
                            bgcolor: '#ffe566',
                            color: '#5a4a00',
                            fontSize: '0.98rem',
                            fontWeight: 900,
                            borderRadius: 2.5,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                        }}
                    >{localize("바꾸기")}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
